import React, { useState } from 'react'
import { Alert, Box, Button, Chip, Collapse, Divider, Stack, Typography } from '@mui/material'
import { Login, Payment } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useCart } from '../../contexts/CartContext'
import LoginDialog from '../auth/LoginDialog'

const CartSummary: React.FC = () => {
  const { state, clearCart, toggleCart } = useCart()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()

  const [loginOpen, setLoginOpen] = useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)

  const formatPrice = (price: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price)

  const handleCheckout = () => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true)
      return
    }
    toggleCart()
    navigate('/checkout')
  }

  if (state.items.length === 0) return null

  return (
    <Box sx={{ p: 3, backgroundColor: 'rgba(0,230,118,0.05)', borderRadius: 2, border: '1px solid rgba(0,230,118,0.2)' }}>
      <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>
        Resumo do pedido
      </Typography>

      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Itens:
          </Typography>
          <Chip label={state.totalItems} size="small" sx={{ bgcolor: '#00e676', color: '#06110b', fontWeight: 900 }} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            Subtotal:
          </Typography>
          <Typography variant="body2" fontWeight={900}>
            {formatPrice(state.total)}
          </Typography>
        </Box>

        <Divider sx={{ opacity: 0.3 }} />

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={900}>
            Total:
          </Typography>
          <Typography variant="h6" fontWeight={900} color="primary">
            {formatPrice(state.total)}
          </Typography>
        </Box>

        <Collapse in={showLoginPrompt && !isAuthenticated}>
          <Alert
            severity="info"
            sx={{ bgcolor: 'rgba(33,150,243,0.12)', border: '1px solid rgba(33,150,243,0.3)' }}
            action={
              <Button color="inherit" size="small" onClick={() => setLoginOpen(true)} sx={{ fontWeight: 900 }}>
                Login
              </Button>
            }
          >
            Faça login para finalizar a compra.
          </Alert>
        </Collapse>

        <Stack spacing={1} sx={{ mt: 1 }}>
          {isAuthenticated ? (
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Payment />}
              onClick={handleCheckout}
              sx={{ py: 1.4, fontWeight: 900, background: 'linear-gradient(45deg, #00e676 30%, #4caf50 90%)' }}
            >
              Finalizar compra
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<Login />}
              onClick={() => setLoginOpen(true)}
              sx={{ py: 1.4, fontWeight: 900, background: 'linear-gradient(45deg, #2196f3 30%, #1976d2 90%)' }}
            >
              Fazer login para comprar
            </Button>
          )}

          <Button fullWidth variant="outlined" size="small" onClick={clearCart} sx={{ color: 'text.secondary' }}>
            Limpar carrinho
          </Button>
        </Stack>
      </Stack>

      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={() => {
          setLoginOpen(false)
          setShowLoginPrompt(false)
          toggleCart()
          navigate('/checkout')
        }}
      />
    </Box>
  )
}

export default CartSummary
